import { getSupabaseClient } from './supabase.client.js';

/**
 * Repositorio de mesas y asignaciones para el panel de staff. Habla con
 * Supabase SOLO a través del cliente anon de navegador (supabase.client.js);
 * el acceso lo enforcea RLS (`mesas_public_read` / `mesas_admin_write` /
 * `reservation_tables_all_staff`). Este es el ÚNICO módulo que emite queries
 * Supabase para mesas.
 *
 * Los errores se re-lanzan preservando el `code` de Postgres. Eso importa: el
 * bloqueo de doble booking llega como `23505` (unique_violation) desde el
 * índice `reservation_tables_no_doble_booking`, y el service necesita
 * distinguirlo de una falla genérica para poder decir QUÉ pasó en vez de
 * "algo salió mal".
 */

/**
 * Re-lanza un error de Supabase preservando el código de Postgres.
 * @param {{ message: string, code?: string }} error
 * @returns {never}
 */
function rethrow(error) {
    const err = new Error(error.message);
    if (error.code) err.code = error.code;
    throw err;
}

/**
 * Todas las mesas activas, ordenadas por posición en la grilla.
 *
 * Si Supabase no está configurado devuelve `[]` — misma degradación que
 * `listBlocks` en blocks.repository.js.
 * @param {{ area?: string }} [params] filtra por área; sin área, trae todas
 * @returns {Promise<Array<object>>}
 * @throws {Error} si Supabase responde con error
 */
export async function listMesas({ area } = {}) {
    const client = getSupabaseClient();
    if (!client) return [];

    let query = client
        .from('mesas')
        .select('*')
        .eq('activa', true)
        .order('grid_row', { ascending: true })
        .order('grid_col', { ascending: true });

    if (area) query = query.eq('area', area);

    const { data, error } = await query;
    if (error) rethrow(error);
    return data ?? [];
}

/**
 * Ocupación de un TURNO completo: qué mesa tiene tomada cada reserva viva ese
 * `(fecha, momento)`. Trae el nombre de la reserva que la ocupa para poder
 * nombrarla al bloquear.
 *
 * @param {{ fecha: string, momento: string }} params
 * @returns {Promise<Array<{ mesa_id: number, reservation_id: string, label: string }>>}
 * @throws {Error} si Supabase responde con error
 */
export async function listOcupacion({ fecha, momento }) {
    const client = getSupabaseClient();
    if (!client) return [];

    const { data, error } = await client
        .from('reservation_tables')
        .select('mesa_id, reservation_id, reservations(nombre, apellido)')
        .eq('fecha', fecha)
        .eq('momento', momento);

    if (error) rethrow(error);

    return (data ?? []).map((row) => ({
        mesa_id: row.mesa_id,
        reservation_id: row.reservation_id,
        label: [row.reservations?.nombre, row.reservations?.apellido].filter(Boolean).join(' '),
    }));
}

/**
 * Asigna una mesa a una reserva. `fecha`/`momento` NO se mandan: el trigger
 * `reservation_tables_fill_slot` los copia de la reserva (migración 16), así
 * que el caller no puede forjarlos para esquivar el índice único.
 *
 * @param {{ reservationId: string, mesaId: number }} params
 * @returns {Promise<void>}
 * @throws {Error} con `code: '23505'` si la mesa ya está tomada en ese turno
 */
export async function assignMesa({ reservationId, mesaId }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Asignación no disponible');

    // `fecha`/`momento` se OMITEN a propósito: son NOT NULL, pero el trigger
    // BEFORE corre antes del chequeo de la constraint, así que los llena él
    // desde la reserva. Mandarlos desde acá sería, en el mejor caso, ruido que
    // el trigger pisa; en el peor, la ilusión de que el cliente decide el
    // turno. (Verificado contra Postgres 17, no asumido.)
    const { error } = await client
        .from('reservation_tables')
        .insert({ reservation_id: reservationId, mesa_id: mesaId });

    if (error) rethrow(error);
}

/**
 * Libera una mesa de una reserva.
 * @param {{ reservationId: string, mesaId: number }} params
 * @returns {Promise<void>}
 * @throws {Error} si Supabase no está configurado o la query falla
 */
export async function unassignMesa({ reservationId, mesaId }) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Liberación no disponible');

    const { error } = await client
        .from('reservation_tables')
        .delete()
        .eq('reservation_id', reservationId)
        .eq('mesa_id', mesaId);

    if (error) rethrow(error);
}

/**
 * Upsert masivo del plano de mesas (sync desde Fudo). La PK es el id de Fudo,
 * así que re-sincronizar es idempotente y nunca duplica.
 *
 * @param {Array<object>} mesas filas ya mapeadas por `mapFudoFloorPlan`
 * @returns {Promise<number>} cantidad de filas upserteadas
 * @throws {Error} si Supabase no está configurado o la query falla
 */
export async function upsertMesas(mesas) {
    const client = getSupabaseClient();
    if (!client) throw new Error('Sincronización no disponible');
    if (mesas.length === 0) return 0;

    const { error } = await client
        .from('mesas')
        .upsert(
            mesas.map((mesa) => ({ ...mesa, synced_at: new Date().toISOString() })),
            { onConflict: 'id' },
        );

    if (error) rethrow(error);
    return mesas.length;
}
